import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/shared/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-paper hover:bg-primary/90 border border-primary',
        destructive: 'bg-error text-paper hover:bg-error/90',
        outline: 'border bg-paper hover:bg-grey-01 hover:text-grey-01-primary',
        secondary: 'bg-secondary hover:bg-secondary/80 text-white',
        ghost: 'hover:bg-grey-01 hover:text-grey-01-primary',
        link: 'text-primary underline-offset-4 hover:underline',
        pill: 'rounded-3xl border border-[#E2E3E6] bg-[rgba(248,248,247,0.5)] hover:border-[#895AD7] hover:bg-[rgba(137,90,215,0.7)]',
      },
      size: {
        default: 'px-3 py-2 leading-6',
        sm: 'rounded-md px-3 py-2',
        lg: 'rounded-md px-8 py-3 leading-7',
        icon: 'rounded-full p-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
    compoundVariants: [
      {
        size: 'icon',
        variant: 'default',
        className: 'bg-transparent hover:bg-grey-01 hover:text-grey-01-primary',
      },
    ],
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
